import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateChildFn, Router, UrlTree } from '@angular/router';
import { ROUTES } from '@constants/routes.enums';
import { ProfileService } from '@services/profile.service';
import { Observable, catchError, filter, map, of, take, timeout } from 'rxjs';

/**
 * Max time (ms) to wait for the profile to load before deciding. If it never
 * resolves we fail open (allow) rather than trap navigation forever — mirrors
 * the consent guard's fail-open philosophy.
 */
const KYC_PROFILE_WAIT_MS = 15_000;

/**
 * KYC consent navigation gate (second child guard on the authenticated shell,
 * after {@link ConsentGuard}). The "Provide Your Consent for KYC Verification"
 * dialog is opened ON the dashboard by {@link DashboardComponent}; this guard
 * keeps a user who still owes DigiLocker/SurePass KYC ON the dashboard — any
 * attempt to reach a deeper route (including programmatic/automation navigation
 * the mandatory modal can't block) is redirected back to the dashboard, which
 * is also the only place the dialog is triggered and the SurePass return
 * completes.
 *
 * It mirrors {@link ConsentGuard}: the dashboard is always allowed immediately,
 * and the deep-route decision AWAITS the profile load rather than reading it
 * synchronously, so fast navigation can't slip through before KYC status is
 * known. Fail-open on timeout/error — a missing profile must never lock the app.
 *
 * NOTE: this is a client-side UX/consistency gate only. Unlike the org consent
 * (enforced server-side via HTTP 451), KYC has no backend boundary, so this
 * guard is not a security control against direct API access.
 */
export const KycConsentGuard: CanActivateChildFn = (
  _childRoute,
  state
): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const profile = inject(ProfileService);
  const router = inject(Router);

  // The dashboard hosts the KYC dialog and is where the SurePass return is
  // processed — always allow it so the dialog can show and the flow can finish.
  if (state.url.startsWith(ROUTES.DASHBOARD)) {
    return true;
  }

  // Profile already loaded → decide synchronously.
  if (profile.profileData()) {
    return profile.kycRequired() ? router.parseUrl(ROUTES.DASHBOARD) : true;
  }

  // Profile not loaded yet (it loads only after the org consent is granted, so
  // this window is brief) → await it instead of failing open during the gap.
  return toObservable(profile.profileData).pipe(
    filter(Boolean),
    take(1),
    timeout({ first: KYC_PROFILE_WAIT_MS }),
    map(() =>
      profile.kycRequired() ? router.parseUrl(ROUTES.DASHBOARD) : true
    ),
    // Profile never resolved → fail open so navigation is never trapped.
    catchError(() => of(true))
  );
};
